// 剣舞の役者の速い連・全身で斬る交互打ちに「踊り」を足した模型（2026-11-04）。
//   作者（11-03 answers/blade-quick-body-mockup-1103）：motion=fix・tempo=220。原文「踊りっぽさが出てない」。
//   斬る動き（踏んでから斬る・腰→肩→腕→剣・重心が前足へ・溜めと振り抜き）は blade_quick_body.ts のまま残し、
//   剣舞のほかの技とワルツ土台から取った5つを足す（docs/research/2026-11-04-blade-quick-dance-mockup.md）：
//     ① 拍で上下する：当たりで沈み（べた足）、打ちの間はつま先へ伸び上がる（ワルツの「下・上」）。
//     ② つま先：浮いた足はつま先を伸ばして運び、つま先から着いて当たりでかかとを下ろす。
//     ③ 腕と剣の線：斬らない方の腕は顔の前に畳まず、後ろ上へ長く開いて剣と1本の線にする。
//        当たりの瞬間はいつも「前下の剣・後ろ上の剣」の斜めの線になり、開いた腕がそのまま次の振りかぶりになる。
//     ④ 回らずに体の向きを切り返す：肩と腰のひねりを打つたびに左右へ返す（前の版のまま）。
//     ⑤ 決め：合図は伸び上がって頭の上で X を見せて止め、沈んで踏み込む。4発目の後は前足のつま先で立ち、
//        後ろ足を伸ばして2本の剣の斜めの線で止まってから構えへ戻る。
//   拍：溜め・当たり・戻り・踏み込みは実機の速い連と同じ。1打ごとの間は作者が選んだ 0.22秒。

import { humanPose, type HumanRig } from '../body'
import { bakeTracks, type MockupMove, type TrackKey, type Tracks } from '../core'
import { BLADE_QUICK_BODY_BEAT } from './blade_quick_body'

/** 1打ごとの間（作者 11-03 tempo=220） */
export const BLADE_QUICK_DANCE_STEP = 220
const B = BLADE_QUICK_BODY_BEAT
const LEAD = 400
const DECIDE_HOLD = 420 // 終わりの決めで止まる長さ

// 腕の形（二の腕 a・前腕 b・剣 s の角度・0＝前・90＝下）。1周ごとに3つとも 360 足して同じ向きに回す。
type Arm = { a: number; b: number; s: number }
const ARM = {
  low: { a: 70, b: 40, s: 30 },
  lowB: { a: 80, b: 50, s: 20 },
  cross: { a: -100, b: -112, s: 300 },  // 合図：頭の上で X（手前の剣は前上へ）
  crossB: { a: -78, b: -66, s: 240 },   // 合図：奥の剣は後ろ上へ
  coil: { a: -130, b: -200, s: 215 },   // 1発目の振りかぶり
  open: { a: -140, b: -145, s: 225 },   // 開いた線：腕と剣を後ろ上へ1本に伸ばす（もう片方が斬っている間）
  deep: { a: -128, b: -215, s: 200 },   // 溜めの底：開いた腕を頭の後ろへ畳む
  hit: { a: 10, b: 30, s: 405 },        // 当たり：剣は前下 45°
  follow: { a: 70, b: 110, s: 480 },    // 振り抜き：体の向こうの下まで流し、そのまま後ろ上の「開いた線」へ（次の周の open）
} satisfies Record<string, Arm>

export function makeBladeQuickDance(stepMs = BLADE_QUICK_DANCE_STEP): MockupMove {
  const S = stepMs
  const T = (i: number) => LEAD + B.chargeMs + i * S
  const t0 = T(0), lastEnd = T(B.hits - 1) + B.activeMs
  const end = T(B.hits - 1) + 0.35 * S
  const decideAt = end + 200, settleAt = decideAt + DECIDE_HOLD, restAt = settleAt + 380
  const durMs = restAt + 300

  // 足先（つま先の床の上の x）。手前の足が1・3発目、奥の足が2・4発目で前へ出る（歩幅 18・最初は踏み込みで 38）。
  const stepX: [number, number][] = []
  let fFx = 6, fBx = -4
  for (let i = 0; i < B.hits; i++) {
    const near = i % 2 === 0, from = near ? fFx : fBx, to = i === 0 ? 34 : (near ? fBx : fFx) + 18
    stepX.push([from, to]); if (near) fFx = to; else fBx = to
  }
  const pxAt = (i: number) => { const front = stepX[i]![1], rear = i === 0 ? -4 : stepX[i - 1]![1]; return rear + 0.62 * (front - rear) }

  const tr: Tracks = { px: [], py: [], lean: [], tw: [], ht: [], aF: [], bF: [], sF: [], aB: [], bB: [], sB: [], fFx: [], fFy: [], fBx: [], fBy: [], rF: [], rB: [] }
  const k = (name: string, at: number, v: number, e?: TrackKey[2]) => tr[name]!.push([at, v, e])
  const arm = (side: 'F' | 'B', at: number, a: Arm, turn: number, e?: TrackKey[2]) => {
    k('a' + side, at, a.a + 360 * turn, e); k('b' + side, at, a.b + 360 * turn, e); k('s' + side, at, a.s + 360 * turn, e)
  }

  // ⑤ 合図：つま先へ伸び上がって頭の上で X を見せて止まり、沈んでから踏み込む
  const up = LEAD + 260, drop = t0 - B.lungeMs
  k('px', 0, 0); k('px', up, -2, 'io'); k('px', drop, -6, 'io')
  k('py', 0, -73); k('py', up, -80, 'io'); k('py', drop - 60, -80); k('py', drop, -67, 'in')
  k('lean', 0, 3); k('lean', up, -4, 'io'); k('lean', drop - 60, -4); k('lean', drop, -6, 'io')
  k('tw', 0, 0); k('tw', up, -8, 'io'); k('tw', drop, -14, 'io')
  k('ht', 0, 0); k('ht', up, -4, 'io'); k('ht', drop, -10, 'io')
  k('rF', 0, 0); k('rF', up, 0.85, 'io'); k('rF', drop - 60, 0.85); k('rF', drop, 0, 'in')
  k('rB', 0, 0); k('rB', up, 0.85, 'io'); k('rB', drop - 60, 0.85); k('rB', drop, 0, 'in')
  arm('F', 0, ARM.low, 0); arm('F', up, ARM.cross, 0, 'io'); arm('F', drop - 60, { ...ARM.cross, a: -104 }, 0)
  arm('B', 0, ARM.lowB, 0); arm('B', up, ARM.crossB, 0, 'io'); arm('B', drop - 60, { ...ARM.crossB, a: -74 }, 0)
  k('fFx', 0, 6); k('fFy', 0, 0); k('fBx', 0, -4); k('fBy', 0, 0)

  for (let i = 0; i < B.hits; i++) {
    const near = i % 2 === 0, sg = near ? 1 : -1, X = near ? 'F' : 'B', t = T(i), first = i === 0
    const turn = Math.floor(i / 2)
    // 踏んでから斬る：斬る側の足を上げ、② つま先を伸ばして運び、つま先から着いて当たりでかかとを下ろす
    const [from, to] = stepX[i]!, lift = first ? drop : t - 0.8 * S, land = t - 0.12 * S
    k('f' + X + 'x', lift, from); k('f' + X + 'x', land, to, 'io')
    k('f' + X + 'y', lift, 0); k('f' + X + 'y', (lift + land) / 2, -9, 'out'); k('f' + X + 'y', land, 0, 'in')
    if (!first) k('r' + X, lift, 0.8, 'io')
    k('r' + X, (lift + land) / 2, 1, 'io'); k('r' + X, land, 0.4, 'io'); k('r' + X, t, 0, 'in')
    // 着いた足は打ちの間につま先へ伸び上がり、次の打ちでは後ろ足としてかかとを上げたまま押し出す
    k('r' + X, t + 0.5 * S, 0.5, 'out')
    // 重心は前足へ（前の版のまま）。① 当たりで沈み、打ちの間は伸び上がる
    const px0 = pxAt(i)
    k('px', t, px0 - 3, 'io'); k('px', t + 0.35 * S, px0, 'out')
    if (!first) k('py', land, -75, 'io')
    k('py', t, -65, 'in'); k('py', t + 0.5 * S, -76, 'out')
    k('lean', t - 0.3 * S, 3, 'io'); k('lean', t, 13, 'in'); k('lean', t + 0.35 * S, 14, 'out'); k('lean', t + 0.7 * S, 3, 'io')
    // ④ ひねり：腰が先・肩が後（前の版のまま）。打つたびに左右へ返す
    k('tw', t - 0.3 * S, -42 * sg, 'io'); k('tw', t, 26 * sg, 'in'); k('tw', t + 0.35 * S, 40 * sg, 'out')
    k('ht', t - 0.3 * S, -4 * sg, 'io'); k('ht', t, 22 * sg, 'in'); k('ht', t + 0.35 * S, 24 * sg, 'out')
    // ③ 斬る腕：（開いた線→）溜めの底→当たり→振り抜き→後ろ上の開いた線（もう片方の当たりの瞬間）
    if (first) arm(X, t - 0.55 * B.lungeMs, ARM.coil, 0, 'io')
    arm(X, t - 0.3 * S, ARM.deep, turn, 'io'); arm(X, t, ARM.hit, turn, 'in'); arm(X, t + 0.35 * S, ARM.follow, turn, 'out')
    if (i < B.hits - 1) arm(X, t + S, ARM.open, turn + 1, 'io')
  }
  // 奥の腕は1発目の当たりで合図の X から開いた線へ
  arm('B', t0, ARM.open, 0, 'io')

  // ⑤ 決め：4発目（奥の剣）を前に残し、手前の剣は後ろ上の線のまま。前足（奥の足）のつま先で立ち、後ろ足（手前の足）を伸ばして後ろへ置く
  const [, front] = stepX[B.hits - 1]!, [, rearX] = stepX[B.hits - 2]!
  k('fFx', end + 20, rearX); k('fFx', decideAt, front - 32, 'io')
  k('fFy', end + 20, 0); k('fFy', (end + 20 + decideAt) / 2, -5, 'out'); k('fFy', decideAt, 0, 'in')
  k('rF', end + 20, 0.7, 'io'); k('rF', decideAt, 1, 'io'); k('rF', settleAt, 1)
  k('rB', decideAt, 0.7, 'io'); k('rB', settleAt, 0.7)
  k('px', decideAt, front - 4, 'io'); k('px', settleAt, front - 4)
  k('py', decideAt, -77, 'out'); k('py', settleAt, -77)
  k('lean', decideAt, -2, 'io'); k('lean', settleAt, -2)
  k('tw', decideAt, -18, 'io'); k('tw', settleAt, -18); k('ht', decideAt, -8, 'io'); k('ht', settleAt, -8)
  arm('B', decideAt, { a: 20, b: 25, s: 380 }, 1, 'io'); arm('B', settleAt, { a: 20, b: 25, s: 380 }, 1)
  arm('F', decideAt, { a: -135, b: -140, s: 230 }, 2, 'io'); arm('F', settleAt, { a: -135, b: -140, s: 230 }, 2)
  // 戻り：後ろ足を引き寄せ、かかとを下ろし、剣を下げて構え
  k('fFx', settleAt + 40, front - 32); k('fFx', restAt - 80, front - 8, 'io')
  k('fFy', settleAt + 40, 0); k('fFy', (settleAt + restAt) / 2 - 20, -5, 'out'); k('fFy', restAt - 80, 0, 'in')
  k('rF', restAt - 80, 0, 'io'); k('rB', restAt, 0, 'io')
  k('px', restAt, front - 4, 'io'); k('py', restAt, -73, 'io'); k('lean', restAt, 3, 'io')
  k('tw', restAt, 0, 'io'); k('ht', restAt, 0, 'io')
  arm('F', restAt, { a: 70, b: 40, s: 30 + 360 }, 1, 'io'); arm('B', restAt, { ...ARM.lowB, s: ARM.lowB.s + 360 }, 1, 'io')
  for (const name in tr) { tr[name]!.sort((a, b) => a[0] - b[0]); tr[name]!.push([durMs, tr[name]!.at(-1)![1]]) }

  const keys = bakeTracks(tr, durMs, v => humanPose({ ...v, fF: [v.fFx, v.fFy], fB: [v.fBx, v.fBy] } as unknown as HumanRig))
  const sec = (S / 1000).toFixed(2)
  return {
    id: `blade-quick-dance-${S}`, boss: '剣舞の役者', label: `速い連・踊りを足した交互打ち（1打 ${sec}秒）`, body: 'human-dual-dress',
    durMs, scale: 0.8, baseX: 332, heroAt: 192, keys, interp: 'linear',
    phases: [
      { from: 0, to: LEAD, label: '構え', kind: 'rest' },
      { from: LEAD, to: drop, label: '合図：つま先へ伸び上がり、頭の上で2本を交差して止める', kind: 'tell' },
      { from: drop, to: t0, label: '沈んで踏み込む', kind: 'tell' },
      { from: t0, to: lastEnd, label: `手前・奥・手前・奥（当たりで沈み、間で伸び上がる・約${sec}秒ごと）`, kind: 'hit' },
      { from: lastEnd, to: settleAt, label: '決め：前足のつま先で立ち、2本の剣の斜めの線で止まる', kind: 'rest' },
      { from: settleAt, to: durMs, label: '戻り：足を引き寄せて構え', kind: 'rest' },
    ],
    hits: Array.from({ length: B.hits }, (_, i) => T(i)), activeMs: B.activeMs,
    box: [22, 100, -150, -40], paths: ['sF', 'sB'], fadeOutMs: 300,
    source: `溜め 0.70秒・当たり 0.12秒・戻り 0.70秒は実機の速い連と同じ。1打ごとの間は作者が選んだ ${sec}秒（実機 0.30秒）`,
  }
}

export const BLADE_QUICK_DANCE_MOCKUP = makeBladeQuickDance()
